/**
 * Anchored VWAP — the maths, shared by the renderer and the hit-tester (DRAW-AVWAP-1).
 *
 * An anchored VWAP is not a set of points the user placed: it is a CURVE computed from the candles,
 *   VWAP(i) = sum(typicalPrice * volume) / sum(volume)   from the anchor bar to bar i,
 * so a drawing stores only `anchorTime`. It used to be computed inline in the page's draw function, which
 * left nothing to hit-test or to hang a handle on. Computing it in ONE place means what is drawn and what
 * can be clicked cannot drift apart.
 *
 * Volume-less bars (the live index bar has none) weigh 1, exactly as the renderer always did.
 * Pure: no DOM, no module state (this module loads on every page via nc-core.js).
 */

export interface VwapCandle {
  time: number;
  high: number;
  low: number;
  close: number;
}

export interface AnchoredVwapSeries {
  /** Index in `candles` of the first bar at or after the anchor; candles.length if there is none. */
  start: number;
  /** values[j] is the VWAP at candles[start + j]. values[0] is the anchor bar's own typical price. */
  values: number[];
}

/**
 * VWAP from the first candle at or after `anchorTime` to the last candle.
 * `volumeByTime` maps bar time -> volume; missing / zero / non-finite volume weighs 1.
 * Candles must be in ascending time order.
 */
export function computeAnchoredVwap(
  candles: VwapCandle[] | null | undefined,
  volumeByTime: Record<number, number> | null | undefined,
  anchorTime: number,
): AnchoredVwapSeries {
  const n = candles ? candles.length : 0;
  if (!candles || !n || !Number.isFinite(anchorTime)) return { start: n, values: [] };
  const vols = volumeByTime || {};
  const values: number[] = [];
  let start = n;
  let cumPV = 0;
  let cumV = 0;
  for (let i = 0; i < n; i++) {
    const c = candles[i];
    if (c.time < anchorTime) continue;
    if (start === n) start = i;
    const vol = vols[c.time] || 1;
    cumPV += ((c.high + c.low + c.close) / 3) * vol;
    cumV += vol;
    values.push(cumPV / cumV);
  }
  return { start, values };
}

/**
 * Keeps an anchor on the data. Dragging the anchor into future whitespace (or before the first bar) would
 * leave a VWAP with no bars — nothing to draw and nothing to click, so it could never be found again.
 */
export function clampAnchorTime(
  anchorTime: number,
  candles: Array<{ time: number }> | null | undefined,
): number {
  if (!candles || !candles.length || !Number.isFinite(anchorTime)) return anchorTime;
  const first = candles[0].time;
  const last = candles[candles.length - 1].time;
  return Math.min(Math.max(anchorTime, first), last);
}

/** The VWAP at the anchor bar (its typical price), or null when the anchor has no bars. */
export function anchorVwapValue(
  candles: VwapCandle[] | null | undefined,
  volumeByTime: Record<number, number> | null | undefined,
  anchorTime: number,
): number | null {
  const s = computeAnchoredVwap(candles, volumeByTime, anchorTime);
  return s.values.length ? s.values[0] : null;
}
